import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import ModalMotionShell from "./ModalMotionShell";
import ModalDragHandle from "./ModalDragHandle";

function useDraggable() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.button !== 0) return;

    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;

    const onMove = (ev) => setPos({ x: ev.clientX - startX, y: ev.clientY - startY });

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return { ref, pos, onMouseDown, setPos };
}

function useIsHandheldModal(open) {
  const [isHandheld, setIsHandheld] = useState(false);

  useEffect(() => {
    if (!open) return;

    const mediaQuery = window.matchMedia?.("(pointer: coarse)");

    const compute = () => {
      const isSmall = window.innerWidth < 768;
      setIsHandheld(Boolean(mediaQuery?.matches && isSmall));
    };

    compute();

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener("change", compute);
    } else if (mediaQuery?.addListener) {
      mediaQuery.addListener(compute);
    }

    window.addEventListener("resize", compute);

    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener("change", compute);
      } else if (mediaQuery?.removeListener) {
        mediaQuery.removeListener(compute);
      }
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  return isHandheld;
}

export default function LegalNoticesModal({ open, onClose, language = "de" }) {
  const drag = useDraggable();
  const isEn = language === "en";
  const isHandheld = useIsHandheldModal(open);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) drag.setPos({ x: 0, y: 0 });
  }, [open]);

  return (
    <ModalMotionShell
      isOpen={open}
      onClose={onClose}
      stretch={isHandheld}
      backdropClassName={!isHandheld ? "!px-2 !py-2" : ""}
    >
      <div
        ref={drag.ref}
        className={`bg-white w-full overflow-hidden ${
          isHandheld
            ? "rounded-none shadow-none flex h-full min-h-0 flex-col bg-white"
            : "rounded-2xl shadow-2xl max-w-4xl max-h-[90vh] flex flex-col"
        }`}
        style={
          isHandheld
            ? {
                position: "fixed",
                inset: 0,
                width: "100vw",
                height: "100dvh",
                maxHeight: "100dvh",
                zIndex: 10001,
                paddingTop: "max(env(safe-area-inset-top), 12px)",
                paddingBottom: "max(env(safe-area-inset-bottom), 12px)",
              }
            : {
                transform: `translate(${drag.pos.x}px, ${drag.pos.y}px)`,
                zIndex: 1000,
              }
        }
        onMouseDown={(e) => e.stopPropagation()}
        onDragStart={(e) => e.preventDefault()}
      >
        <div className="h-[5px] shrink-0 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600" />

        <div
          className={`modal-header shrink-0 bg-white border-b border-gray-200 ${
            isHandheld ? "px-4 py-3" : "drag-handle sticky top-0 px-6 py-4 cursor-move select-none"
          }`}
          onMouseDown={
            isHandheld
              ? undefined
              : (e) => {
                  if (e.button === 0) {
                    e.preventDefault();
                    e.stopPropagation();
                    drag.onMouseDown(e);
                  }
                }
          }
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {!isHandheld && (
                <ModalDragHandle
                  onMouseDown={drag.onMouseDown}
                  title={isEn ? "Move" : "Verschieben"}
                  ariaLabel={isEn ? "Move modal" : "Modal verschieben"}
                  className="mt-0.5"
                />
              )}
              <h2 className={`font-semibold text-gray-900 ${isHandheld ? "text-lg" : "text-xl"}`}>
                {isEn ? "Legal Notices & Privacy Policy" : "Rechtliche Hinweise & Datenschutz"}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="group text-gray-400 hover:text-gray-600 transition p-2 -m-2 flex-shrink-0"
              aria-label={isEn ? "Close" : "Schließen"}
            >
              <X className="w-5 h-5 transition-transform duration-200 group-hover:rotate-90" />
            </button>
          </div>
        </div>

        <div className={`space-y-8 overflow-y-auto overscroll-contain flex-1 text-sm min-h-0 ${
          isHandheld ? "px-4 py-4 pb-6" : "px-6 py-6"
        }`}>
          {isEn ? (
            <>
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Service Description & Disclaimer</h3>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    V3Tax is a blockchain analysis tool designed to assist users in preparing their personal tax returns.
                    The service provides automated calculations and classifications of cryptocurrency and NFT transactions
                    based on generally recognized tax principles.
                  </p>
                  <p className="font-semibold text-red-600">
                    ⚠️ IMPORTANT: This tool does NOT constitute professional tax, legal, or financial advice.
                    All calculations and classifications are provided for informational purposes only.
                  </p>
                  <p>
                    Tax laws may vary by country and are subject to change at any time.
                    Users bear full responsibility for compliance with tax and legal regulations in their respective jurisdictions.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Limitation of Liability</h3>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    The service assumes no liability for losses, damages, tax disadvantages, sanctions, or other consequences
                    arising from the use of this tool or reliance on the information provided.
                  </p>
                  <p>
                    We make no representations regarding the accuracy, completeness, or suitability of the analyses
                    for specific tax or legal requirements.
                  </p>
                  <p className="font-medium">
                    Users should always consult qualified tax advisors, accountants, or legal advisors.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Data Processing & Privacy</h3>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    To provide calculations and reports, wallet addresses, transaction histories, uploaded CSV files and related metadata may be processed temporarily.
                  </p>
                  <p>
                    We process personal data only to the extent required to operate the service, provide support, and prevent abuse. Further details are governed by the applicable privacy rules and technical safeguards.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4. Third-Party Data & Integrations</h3>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    On-chain transfer history is retrieved from VeChain data sources and third-party interfaces.
                  </p>
                  <p>
                    For price valuation, V3Tax uses a multi-source pricing model. Broadly listed assets may be valued using Tier-1 market data such as CoinGecko. For supported VeChain-specific assets, historical DEX data and supplemental ecosystem pricing sources may also be processed.
                  </p>
                  <p>
                    V3Tax only uses token prices within defined quality and plausibility limits for automatic tax calculation. Depending on the source, exact or nearby price anchors, interpolation, or validated daily/DEX data may be used. If no sufficiently reliable token price is available for a transaction, the transaction is excluded from the calculation due to an unreliable token price.
                  </p>
                  <p>
                    VeChainStats, CoinGecko and other data providers are independent third-party providers. References to these providers, including logos and brand names, are used solely for source attribution and identification and do not imply partnership, sponsorship, endorsement, certification, or official support of the service.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5. Availability</h3>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    We strive to provide the service with the highest possible continuity, but we do not guarantee uninterrupted availability. Planned maintenance may be announced in advance where possible.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">6. Trademarks & Logos</h3>
                <p className="text-gray-700 leading-relaxed">
                  Trademarks and logos displayed in the service, including references to the VeChain Foundation, remain the property of their respective owners. Their display serves identification purposes only and does not imply affiliation or official endorsement.
                </p>
              </section>

              <div className="text-sm text-gray-500 mt-8">Last updated: May 2, 2026</div>
            </>
          ) : (
            <>
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Leistungsbeschreibung & Haftungsausschluss</h3>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    V3Tax ist ein Blockchain-Analyse-Tool zur Unterstützung bei der Vorbereitung privater Steuererklärungen. Der Dienst stellt automatisierte Berechnungen und Klassifizierungen von Krypto- und NFT-Transaktionen auf Basis allgemein anerkannter steuerlicher Grundsätze bereit.
                  </p>
                  <p className="font-semibold text-red-600">
                    ⚠️ WICHTIG: Dieses Tool stellt KEINE professionelle steuerliche, rechtliche oder finanzielle Beratung dar. Alle Berechnungen und Klassifizierungen dienen ausschließlich Informationszwecken.
                  </p>
                  <p>
                    Steuerregeln können je nach Land abweichen und sich jederzeit ändern. Nutzer tragen die volle Verantwortung für die Einhaltung steuerlicher und rechtlicher Vorschriften.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Haftungsbeschränkung</h3>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    Der Dienst übernimmt keine Haftung für Verluste, Schäden, steuerliche Nachteile, Sanktionen oder sonstige Folgen, die aus der Nutzung dieses Tools oder dem Vertrauen auf die bereitgestellten Informationen entstehen.
                  </p>
                  <p>
                    Es wird keine Gewähr für Richtigkeit, Vollständigkeit oder Eignung der Analysen für konkrete steuerliche oder rechtliche Anforderungen übernommen.
                  </p>
                  <p className="font-medium">
                    Nutzer sollten stets qualifizierte Steuerberater, Buchhalter oder Rechtsberater hinzuziehen.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Datenverarbeitung & Datenschutz</h3>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    Zur Erstellung von Berechnungen und Reports können Wallet-Adressen, Transaktionshistorien, hochgeladene CSV-Dateien und zugehörige Metadaten vorübergehend verarbeitet werden.
                  </p>
                  <p>
                    Personenbezogene Daten werden nur in dem Umfang verarbeitet, der für den Betrieb des Dienstes, Support-Leistungen und Missbrauchsprävention erforderlich ist.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4. Drittanbieter-Daten & Integrationen</h3>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    Die On-chain-Transferhistorie wird über VeChain-Datenquellen und Drittanbieter-Schnittstellen bezogen.
                  </p>
                  <p>
                    Für die Preisbewertung verwendet V3Tax ein mehrstufiges Quellenmodell. Breit gelistete Assets können über Tier-1-Marktdaten wie CoinGecko bewertet werden. Für unterstützte VeChain-spezifische Assets können außerdem historische DEX-Daten und ergänzende Ökosystem-Preisquellen verarbeitet werden.
                  </p>
                  <p>
                    V3Tax verwendet für die automatische Steuerberechnung nur Token-Preise innerhalb definierter Qualitäts- und Plausibilitätsgrenzen. Je nach Quelle können exakte oder nahe Kursstützpunkte, Interpolation oder geprüfte Tages-/DEX-Daten genutzt werden. Wenn für eine Transaktion kein ausreichend verlässlicher Token-Preis verfügbar ist, wird diese Transaktion wegen nicht belastbarem Token-Preis von der Berechnung ausgeschlossen.
                  </p>
                  <p>
                    VeChainStats, CoinGecko und weitere Datenanbieter sind unabhängige Drittanbieter. Die Nennung dieser Anbieter, einschließlich Logos und Markennamen, dient ausschließlich der Quellenangabe und Identifikation und impliziert keine Partnerschaft, kein Sponsoring, keine Empfehlung, keine Zertifizierung und keine offizielle Unterstützung des Dienstes.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5. Verfügbarkeit</h3>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    Wir bemühen uns um eine möglichst unterbrechungsfreie Bereitstellung des Dienstes, übernehmen jedoch keine Garantie für durchgehende Verfügbarkeit. Geplante Wartungsarbeiten werden nach Möglichkeit im Voraus angekündigt.
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">6. Marken & Logos</h3>
                <p className="text-gray-700 leading-relaxed">
                  Die im Dienst angezeigten Marken und Logos, einschließlich der VeChain Foundation, bleiben Eigentum der jeweiligen Rechteinhaber. Ihre Anzeige dient ausschließlich der Identifikation und impliziert keine Zugehörigkeit oder offizielle Unterstützung.
                </p>
              </section>

              <div className="text-sm text-gray-500 mt-8">Zuletzt aktualisiert: 2. Mai 2026</div>
            </>
          )}
        </div>

        <div
          className={`shrink-0 border-t border-gray-200 bg-white ${
            isHandheld ? "px-4 py-3" : "flex justify-end px-6 py-4"
          }`}
        >
          <div className={isHandheld ? "flex justify-end" : ""}>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-blue-600 px-6 py-2 font-medium text-white transition hover:bg-blue-700"
            >
              {isEn ? "Close" : "Schließen"}
            </button>
          </div>
        </div>
      </div>
    </ModalMotionShell>
  );
}
